import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { paymentsAPI } from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, Loader2, XCircle, CalendarCheck, UserPlus, Eye, EyeOff } from 'lucide-react';
import Navbar from '../components/Navbar';
import { formatPrice } from '../lib/utils';
import { trackPurchase } from '../lib/analytics';
import { toast, Toaster } from 'sonner';

// ==================== SUB-COMPONENTS ====================

const LoadingState = () => (
  <>
    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
      <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
    </div>
    <h1 className="font-heading text-2xl font-bold text-neutral-900 mb-2">
      Processing Payment...
    </h1>
    <p className="text-neutral-600">
      Please wait while we confirm your payment.
    </p>
  </>
);

const PaymentAmountDisplay = ({ amount, isSubscription }) => {
  // Handle free registrations (amount = 0) or undefined amounts
  if (amount === undefined || amount === null || amount === 0) {
    return (
      <div className="bg-neutral-50 rounded-lg p-4 mb-6">
        <p className="text-sm text-neutral-500">Amount</p>
        <p className="text-2xl font-mono font-bold text-green-600">FREE</p>
      </div>
    );
  }
  
  return (
    <div className="bg-neutral-50 rounded-lg p-4 mb-6">
      <p className="text-sm text-neutral-500">
        {isSubscription ? 'Monthly Amount' : 'Amount Paid'}
      </p>
      <p className="text-2xl font-mono font-bold text-green-600">
        {formatPrice(amount / 100)}
        {isSubscription && <span className="text-sm text-neutral-500 font-normal">/month</span>}
      </p>
    </div>
  );
};

const AccountCreationForm = ({
  accountForm,
  setAccountForm,
  showPassword,
  setShowPassword,
  creatingAccount,
  onSubmit
}) => (
  <div className="bg-purple-50 rounded-lg p-6 mb-6 text-left">
    <div className="flex items-center gap-2 mb-4">
      <UserPlus className="w-5 h-5 text-purple-600" />
      <h3 className="font-semibold text-neutral-900">Create Your Account</h3>
    </div>
    <p className="text-sm text-neutral-600 mb-4">
      Create an account to view your bookings, manage payments, and receive updates about your child's classes.
    </p>
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="first_name" className="text-xs">First Name</Label>
          <Input
            id="first_name"
            placeholder="First name"
            value={accountForm.first_name}
            onChange={(e) => setAccountForm({...accountForm, first_name: e.target.value})}
            required
            className="h-9"
            data-testid="guest-firstname"
          />
        </div>
        <div>
          <Label htmlFor="last_name" className="text-xs">Last Name</Label>
          <Input
            id="last_name"
            placeholder="Last name"
            value={accountForm.last_name}
            onChange={(e) => setAccountForm({...accountForm, last_name: e.target.value})}
            required
            className="h-9"
            data-testid="guest-lastname"
          />
        </div>
      </div>
      <div>
        <Label htmlFor="email" className="text-xs">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="you@example.com"
          value={accountForm.email}
          onChange={(e) => setAccountForm({...accountForm, email: e.target.value})}
          required
          className="h-9"
          data-testid="guest-email"
        />
      </div>
      <div>
        <Label htmlFor="password" className="text-xs">Password</Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Min 8 characters"
            value={accountForm.password}
            onChange={(e) => setAccountForm({...accountForm, password: e.target.value})}
            required
            minLength={8}
            className="h-9 pr-10"
            data-testid="guest-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div>
        <Label htmlFor="confirmPassword" className="text-xs">Confirm Password</Label>
        <Input
          id="confirmPassword"
          type={showPassword ? 'text' : 'password'}
          placeholder="Confirm password"
          value={accountForm.confirmPassword}
          onChange={(e) => setAccountForm({...accountForm, confirmPassword: e.target.value})}
          required
          className="h-9"
          data-testid="guest-confirm-password"
        />
      </div>
      <Button 
        type="submit" 
        className="w-full bg-purple-600 hover:bg-purple-700"
        disabled={creatingAccount}
        data-testid="guest-create-account"
      >
        {creatingAccount ? 'Creating Account...' : 'Create Account & View Booking'}
      </Button>
    </form>
  </div>
);

const SuccessState = ({
  isSubscription,
  isOneOff,
  paymentInfo,
  showAccountForm,
  isAuthenticated,
  accountForm,
  setAccountForm,
  showPassword,
  setShowPassword,
  creatingAccount,
  handleCreateAccount,
  navigate
}) => {
  const getTitle = () => {
    if (isSubscription) return 'Subscription Started!';
    if (isOneOff) return 'One-Off Class Booked!';
    return 'Payment Successful!';
  };

  const getMessage = () => {
    if (isSubscription) return "Your monthly subscription is now active. You'll be charged automatically each month.";
    if (isOneOff) return "Your drop-in class booking has been confirmed. We'll see you there!";
    return "Your booking has been confirmed. We've sent you an email with the details.";
  };

  return (
    <>
      <div className={`w-20 h-20 ${isOneOff ? 'bg-orange-100' : 'bg-green-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
        <CheckCircle className={`w-12 h-12 ${isOneOff ? 'text-orange-600' : 'text-green-600'}`} />
      </div>
      <h1 className="font-heading text-2xl font-bold text-neutral-900 mb-2">
        {getTitle()}
      </h1>
      <p className="text-neutral-600 mb-6">
        {getMessage()}
      </p>
      
      {paymentInfo && (
        <PaymentAmountDisplay amount={paymentInfo.amount_total} isSubscription={isSubscription} />
      )}
      
      {showAccountForm && !isAuthenticated && (
        <AccountCreationForm
          accountForm={accountForm}
          setAccountForm={setAccountForm}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          creatingAccount={creatingAccount}
          onSubmit={handleCreateAccount}
        />
      )}
      
      {isSubscription && (
        <div className="flex items-center justify-center gap-2 text-sm text-neutral-500 mb-4">
          <CalendarCheck className="w-4 h-4" />
          <span>Next payment in 30 days</span>
        </div>
      )}
      
      {(!showAccountForm || isAuthenticated) && (
        <div className="flex gap-4 justify-center">
          <Button onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard">
            View My {isSubscription ? 'Subscriptions' : 'Bookings'}
          </Button>
          <Button variant="outline" onClick={() => navigate('/classes')}>
            Browse More Classes
          </Button>
        </div>
      )}
    </>
  );
};

const ErrorState = ({ status, navigate }) => (
  <>
    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
      <XCircle className="w-12 h-12 text-red-600" />
    </div>
    <h1 className="font-heading text-2xl font-bold text-neutral-900 mb-2">
      {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
    </h1>
    <p className="text-neutral-600 mb-6">
      {status === 'expired' 
        ? 'Your payment session has expired. Please try booking again.'
        : "We couldn't verify your payment. Please contact support if you were charged."}
    </p>
    <div className="flex gap-4 justify-center">
      <Button onClick={() => navigate('/classes')}>
        Try Again
      </Button>
      <Button variant="outline" onClick={() => navigate('/dashboard')}>
        Go to Dashboard
      </Button>
    </div>
  </>
);

// ==================== MAIN COMPONENT ====================

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  
  // URL params
  const sessionId = searchParams.get('session_id');
  const isSubscription = searchParams.get('subscription') === 'true';
  const isOneOff = searchParams.get('one_off') === 'true';
  const isGuest = searchParams.get('guest') === 'true';
  const isRegistration = searchParams.get('registration') === 'true';
  const pendingId = searchParams.get('pending_id');
  const isPublicRegistration = searchParams.get('public') === 'true';
  
  // Account creation state
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [accountForm, setAccountForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);

  useEffect(() => {
    console.log('PaymentSuccess: Page loaded with params:', { sessionId, isSubscription, isGuest, isRegistration, pendingId, isPublicRegistration, noPayment: searchParams.get('no_payment') });
    console.log('PaymentSuccess: API URL is:', process.env.REACT_APP_BACKEND_URL);
    console.log('PaymentSuccess: isAuthenticated:', isAuthenticated);
    
    const noPayment = searchParams.get('no_payment') === 'true';
    const registrationToken = searchParams.get('registration_token');
    
    if (noPayment && registrationToken) {
      // Free registration - no payment needed, show account creation
      console.log('PaymentSuccess: Free registration detected, showing account form');
      setStatus('success');
      setShowAccountForm(true);
      setPaymentInfo({ payment_status: 'paid', amount_total: 0, registration_token: registrationToken });
    } else if (isPublicRegistration && pendingId && sessionId) {
      // Public registration - complete registration after checking payment
      handlePublicRegistrationCompletion();
    } else if (sessionId) {
      pollPaymentStatus();
    } else {
      console.error('PaymentSuccess: No session_id in URL params!');
      setStatus('error');
    }
  }, [sessionId]);

  const handlePublicRegistrationCompletion = async () => {
    try {
      // First check payment status
      const statusResponse = await api.get(`/register/payment-status/${pendingId}`);
      console.log('Public registration payment status:', statusResponse.data);
      
      if (statusResponse.data.payment_status === 'paid') {
        // GTM purchase event — Stripe amount_total is in pence
        trackPurchase((statusResponse.data.amount_total || 0) / 100, sessionId || pendingId);

        // Complete the registration (creates user account and booking)
        const completeResponse = await api.post('/register/complete-public', {
          pending_id: pendingId,
          session_id: sessionId
        });
        
        console.log('Public registration completed:', completeResponse.data);

        // GTM conversion event — only when the account was genuinely created now
        if (completeResponse.data.status === 'completed') {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'registration_complete' });
        }

        setPaymentInfo(statusResponse.data);
        setStatus('success');
        setShowAccountForm(false); // Account already created
        
        // Auto-login if credentials are returned
        if (completeResponse.data.email) {
          toast.success('Registration complete! Please log in with your credentials.');
        }
      } else {
        // Payment not yet confirmed, poll for status
        pollPaymentStatus();
      }
    } catch (error) {
      console.error('Error completing public registration:', error);
      // Fall back to polling
      pollPaymentStatus();
    }
  };

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      let response;
      console.log('PaymentSuccess: Checking payment status...', { isRegistration, pendingId, sessionId, isGuest, isAuthenticated });
      
      if (isRegistration && pendingId) {
        console.log('PaymentSuccess: Calling /register/payment-status/' + pendingId);
        response = await api.get(`/register/payment-status/${pendingId}`);
      } else if ((isGuest || isRegistration) && !isAuthenticated) {
        console.log('PaymentSuccess: Calling /payments/guest-status/' + sessionId);
        response = await api.get(`/payments/guest-status/${sessionId}`);
      } else {
        console.log('PaymentSuccess: Calling paymentsAPI.getStatus');
        response = await paymentsAPI.getStatus(sessionId);
      }
      
      console.log('PaymentSuccess: Response received', response.data);
      const data = response.data;
      setPaymentInfo(data);

      if (data.payment_status === 'paid') {
        // GTM purchase event — Stripe amount_total is in pence
        trackPurchase((data.amount_total || 0) / 100, sessionId || pendingId);
        setStatus('success');
        if ((isGuest || isRegistration) && !isAuthenticated) {
          setShowAccountForm(true);
          if (data.customer_email) {
            setAccountForm(prev => ({ ...prev, email: data.customer_email }));
          }
          if (data.guardian_first_name) {
            setAccountForm(prev => ({ 
              ...prev, 
              first_name: data.guardian_first_name,
              last_name: data.guardian_last_name || ''
            }));
          }
        }
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      } else if (data.payment_status === 'error' || data.status === 'error') {
        console.error('Payment verification returned error:', data.message);
        setStatus('error');
        return;
      }

      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      console.error('Error details:', error.response?.data);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } else {
        // Log final error for debugging
        console.error('Payment verification failed after max attempts. Session ID:', sessionId, 'Pending ID:', pendingId);
        setStatus('error');
      }
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    
    if (accountForm.password !== accountForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (accountForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setCreatingAccount(true);
    try {
      const noPayment = searchParams.get('no_payment') === 'true';
      const registrationToken = searchParams.get('registration_token');
      
      if (noPayment && registrationToken) {
        // Free registration - complete without payment
        await api.post('/register/complete-free', {
          token: registrationToken,
          password: accountForm.password
        });
      } else if (isRegistration && pendingId) {
        await api.post('/register/complete-with-payment', {
          pending_id: pendingId,
          password: accountForm.password,
          session_id: sessionId
        });
      } else {
        await api.post('/auth/register-with-booking', {
          email: accountForm.email,
          password: accountForm.password,
          first_name: accountForm.first_name,
          last_name: accountForm.last_name,
          session_id: sessionId
        });
      }

      // GTM conversion event — account has been created at this point
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'registration_complete' });
      
      await login(accountForm.email, accountForm.password);
      toast.success('Account created! Your booking is now linked to your account.');
      setShowAccountForm(false);
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account. Please try again.');
    } finally {
      setCreatingAccount(false);
    }
  };

  // Render based on status
  const renderContent = () => {
    switch (status) {
      case 'loading':
        return <LoadingState />;
      case 'success':
        return (
          <SuccessState
            isSubscription={isSubscription}
            isOneOff={isOneOff}
            paymentInfo={paymentInfo}
            showAccountForm={showAccountForm}
            isAuthenticated={isAuthenticated}
            accountForm={accountForm}
            setAccountForm={setAccountForm}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            creatingAccount={creatingAccount}
            handleCreateAccount={handleCreateAccount}
            navigate={navigate}
          />
        );
      case 'error':
      case 'timeout':
      case 'expired':
        return <ErrorState status={status} navigate={navigate} />;
      default:
        return <LoadingState />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      <Navbar />
      <Toaster position="top-right" richColors />
      
      <div className="max-w-lg mx-auto px-4 py-16">
        <Card className="shadow-xl border-0">
          <CardContent className="p-8 text-center">
            {renderContent()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentSuccess;
